import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  IcLineChart,
  IcBanknote,
  IcBarChart,
  IcCoins,
  IcAlertTriangle,
  IcClock,
  IcClipboardList,
  IcTrophy,
  IcCheckCircle,
  IcCalendarCheck,
  IcUsers,
  IcHome,
  IcWarehouse,
} from '../../theme/icons';
import dashboardService from '../../services/dashboardService';
import SalesTrendChart from '../../components/SalesTrendChart';
import PurchaseSalesBarChart from '../../components/PurchaseSalesBarChart';
import ReservationProductActions from '../../components/ReservationProductActions';
import PhoneLink from '../../components/PhoneLink';

// Thème sombre mobile — Brand Kit §06 (livre 2026-09-04) : fond #0A0A0A,
// cartes #1F2937 (gray-800) bordées #374151 (gray-700) rayon 10 px, espacées
// de 9 px ; titre de carte 12 px 600 #F0FDF4 (green-50) ; cartes KPI bordées
// de leur accent (vert / bleu) avec halo bleu 12 px (0.15 / 0.20 — tel quel
// dans le livre, y compris sur la carte CA bordée vert), Meilleur jour bordé
// amber-600 halo amber-500/18 ; textes #E5E7EB (gray-200) / #F0FDF4,
// secondaires #9CA3AF (gray-400), sous-lignes #6B7280 (gray-500).
// `mk-dark` = repère (sans style) que mobile-theme.css remappe en clair.
const DARK_CARD = 'mk-dark rounded-2xl bg-gray-800 border border-gray-700';
const DARK_ROW = 'mk-dark bg-gray-900 border border-gray-700 rounded-xl';

const formatCurrency = (a) => `${parseFloat(a || 0).toFixed(3)} DT`;
const formatDate = (s) => (s ? new Date(s).toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' }) : '');
const formatTime = (s) => (s ? new Date(s).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '');
const fmtDay = (iso) => {
  const p = String(iso || '').split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}` : String(iso || '');
};
// Podium : or (amber-500) · argent (gray-300) · bronze (amber-700) · reste neutre.
const rankColor = (i) => i === 0 ? 'bg-amber-500 text-white' : i === 1 ? 'bg-gray-300 text-gray-700' : i === 2 ? 'bg-amber-700 text-white' : 'bg-gray-700 text-gray-200';
const statusOrder = (status) => {
  switch (status?.toLowerCase()) {
    case 'scheduled': case 'planifié': return 0;
    case 'in_progress': case 'confirmed': case 'confirmé': return 1;
    case 'completed': case 'terminé': return 2;
    case 'cancelled': case 'annulé': return 3;
    default: return 1;
  }
};
// Étiquettes Ki Para §7 (séance de soin) : programmé / en cours / terminé / annulé.
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'scheduled': case 'planifié': return 'bdg-pos2';
    case 'in_progress': case 'confirmed': case 'confirmé': return 'bdg-live';
    case 'completed': case 'terminé': return 'bdg-done';
    case 'cancelled': case 'annulé': return 'bdg-neutral';
    default: return 'bdg-warn';
  }
};

// Split the daily series into weeks of (up to) 7 days, KEEPING the per-day
// points so the curve can be read day-by-day. Chunked from the most recent day
// backwards, so the last week always holds the latest 7 days (a full "this
// week"); the oldest chunk may be shorter. Weeks are returned chronologically.
const toWeeks = (series) => {
  const weeks = [];
  for (let end = series.length; end > 0; end -= 7) {
    weeks.unshift(series.slice(Math.max(0, end - 7), end));
  }
  return weeks;
};

// "DD/MM – DD/MM" range label for a week's daily points.
const weekLabel = (week) => {
  if (!week || !week.length) return '';
  const f = (iso) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}` : String(iso || ''); };
  return week.length === 1 ? f(week[0].date) : `${f(week[0].date)} – ${f(week[week.length - 1].date)}`;
};

// Section card shell — carte sombre du kit (§06).
function Card({ title, icon: Icon, iconClass, action, children, className = '' }) {
  return (
    <div className={`${DARK_CARD} p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <h2 className="font-semibold text-green-50 flex items-center gap-2 min-w-0">
          {Icon && <Icon className={`${iconClass} shrink-0`} />} <span className="truncate">{title}</span>
        </h2>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function MobileHome() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekIdx, setWeekIdx] = useState(null); // null = default to most recent week

  useEffect(() => {
    let alive = true;
    dashboardService.getDashboardData('30d')
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { console.error('Dashboard load failed:', e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const refreshHome = async () => {
    try { const d = await dashboardService.getDashboardData('30d'); setData(d); }
    catch (e) { console.error('Refresh home failed:', e); }
  };

  const goVentes = () => navigate('/statistiques/ventes');
  const goGenerale = () => navigate('/statistiques/generale');

  const series = Array.isArray(data?.series) ? data.series : [];
  const weeks = toWeeks(series);
  // Default to the most recent week; clamp any stale/out-of-range selection.
  const curWeek = weeks.length ? Math.min(weekIdx ?? weeks.length - 1, weeks.length - 1) : 0;
  const selectedWeek = weeks[curWeek] || [];
  const goWeek = (delta) => (e) => {
    e.stopPropagation();
    setWeekIdx((prev) => {
      const base = prev ?? weeks.length - 1;
      return Math.max(0, Math.min(weeks.length - 1, base + delta));
    });
  };
  const today = series[series.length - 1] || {};
  const dayLabel = today.date ? fmtDay(today.date) : '';

  return (
    <div className="w-full px-4 py-6">
      {/* Nom de page = pill blanche 8/16 px, texte encre 700 13 px, icône
          14 px, écart 6 px, 12 px sous la pill (Brand Kit §06 mock). */}
      <div className="mb-6">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-white text-gray-900">
          <IcHome size={13} /> Dashboard
        </span>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Ventes / jour — clickable curve → detailed sales statistics.
              Carte 11 px, titre 12 px 600 green-50, nav semaine vert #16A34A. */}
          <div
            role="button"
            tabIndex={0}
            onClick={goVentes}
            onKeyDown={(e) => { if (e.key === 'Enter') goVentes(); }}
            className={`${DARK_CARD} cursor-pointer p-3`}
            title="Voir les statistiques de ventes détaillées"
          >
            <div className="flex items-center gap-2 mb-2 px-1">
              <IcLineChart className="text-green-50 shrink-0" />
              <h2 className="font-semibold text-green-50">Ventes / jour</h2>
              {/* Week filter — pick a week, read it day-by-day (vert #16A34A, mock).
                  Flèches = cibles 44 px (règles absolues du frame). */}
              <div className="ml-auto flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={goWeek(-1)}
                  disabled={curWeek <= 0}
                  aria-label="Semaine précédente"
                  className="h-7 w-7 flex items-center justify-center rounded-lg text-green-600 font-bold hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition"
                >‹</button>
                <span className="text-xs font-semibold text-green-600 tabular-nums whitespace-nowrap min-w-[92px] text-center">
                  {weekLabel(selectedWeek) || 'Semaine'}
                </span>
                <button
                  onClick={goWeek(1)}
                  disabled={curWeek >= weeks.length - 1}
                  aria-label="Semaine suivante"
                  className="h-7 w-7 flex items-center justify-center rounded-lg text-green-600 font-bold hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition"
                >›</button>
              </div>
            </div>
            <SalesTrendChart series={selectedWeek} transparent />
            <p className="text-center text-[11px] text-gray-400 mt-1">
              Cliquez sur la courbe pour les statistiques de ventes détaillées →
            </p>
          </div>

          {/* Chiffre d'affaires (bord vert #15803D) + Bénéfice (bord bleu
              #2563EB) — cartes KPI 10 px, écart 8 px, halo bleu 12 px 0.15 /
              0.20 exactement comme le livre §06 ; libellé 10.5 px gray-400,
              valeur 17 px 700 green-50 tabulaire, sous-ligne 10 px gray-500. */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={goVentes}
              className="mk-dark text-left rounded-2xl p-4 bg-gray-800 border border-green-700 shadow-[0_0_12px_theme(colors.blue.600/15%)]"
            >
              <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1.5"><IcBanknote size={12} /> Chiffre d'affaires</div>
              <div className="text-base font-bold text-green-50 truncate tabular-nums" title={formatCurrency(today.ca)}>{formatCurrency(today.ca)}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">{dayLabel ? `Aujourd'hui (${dayLabel})` : 'Aujourd’hui'}</div>
            </button>
            <button
              onClick={goVentes}
              className="mk-dark text-left rounded-2xl p-4 bg-gray-800 border border-blue-600 shadow-[0_0_12px_theme(colors.blue.600/20%)]"
            >
              <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1.5"><IcLineChart size={12} /> Bénéfice</div>
              <div className="text-base font-bold text-green-50 truncate tabular-nums" title={formatCurrency(today.benefice)}>{formatCurrency(today.benefice)}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">{dayLabel ? `Aujourd'hui (${dayLabel})` : 'Aujourd’hui'}</div>
            </button>
          </div>

          {/* Meilleur jour de vente (CA le plus élevé) — 3 horizons (le livre
              n'en montre qu'un), cartes 10 px bordées amber #D97706, halo
              amber-500/18, trophée 17 px amber-500, libellé 10.5 px amber-300,
              montant 700 green-50 · date 11 px 400 gray-400 (mock §06). */}
          <div className="space-y-3">
            {[
              { key: 'week', label: 'Meilleur jour · cette semaine' },
              { key: 'month', label: 'Meilleur jour · ce mois-ci' },
              { key: 'all_time', label: 'Meilleur jour · record absolu' },
            ].map(({ key, label }) => {
              const best = data?.best_days?.[key];
              return (
                <button
                  key={key}
                  onClick={goVentes}
                  className="mk-dark w-full text-left rounded-2xl p-4 bg-gray-800 border border-amber-600 shadow-[0_0_12px_theme(colors.amber.500/18%)] flex items-center gap-3"
                >
                  <IcTrophy className="text-amber-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-amber-300">{label}</div>
                    <div className="text-base font-bold text-green-50 truncate tabular-nums" title={formatCurrency(best?.ca)}>
                      {formatCurrency(best?.ca)}
                      <span className="font-normal text-gray-400 text-sm"> · {best?.date ? formatDate(best.date) : '—'}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Achats vs Ventes / semaine — clickable bar chart → statistiques générales */}
          <div
            role="button"
            tabIndex={0}
            onClick={goGenerale}
            onKeyDown={(e) => { if (e.key === 'Enter') goGenerale(); }}
            className={`${DARK_CARD} cursor-pointer p-3`}
            title="Voir les statistiques générales"
          >
            <div className="flex items-center gap-2 mb-2 px-1">
              <IcBarChart className="text-green-50 shrink-0" />
              <h2 className="font-semibold text-green-50">Achats vs Ventes / semaine</h2>
            </div>
            <PurchaseSalesBarChart data={data?.weekly_compare} transparent />
            <p className="text-center text-[11px] text-gray-400 mt-1">
              Cliquez sur le diagramme pour les statistiques générales →
            </p>
          </div>

          {/* Case Stock — remplace les tuiles « Bénéfice net » et « Clients
              impayés » (Ahmed 2026-08-17) ; même case que la page statistique
              générale, cliquable → statistiques générales. Composant clair
              (carte blanche) gardé sur le fond sombre, comme le site réel. */}
          <div
            role="button"
            tabIndex={0}
            onClick={goGenerale}
            onKeyDown={(e) => { if (e.key === 'Enter') goGenerale(); }}
            className="cursor-pointer bg-white rounded-2xl border border-gray-200 p-4 min-w-0"
            title="Voir les statistiques générales"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="h-7 w-7 shrink-0 rounded-lg flex items-center justify-center bg-amber-50 text-amber-600">
                <IcWarehouse size={12} />
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Stock</span>
            </div>
            <div className="grid grid-cols-2 divide-x divide-gray-100 text-center">
              <div className="pr-3 min-w-0">
                <div className="text-[11px] text-gray-500 mb-0.5">Valeur</div>
                <div className="text-lg font-bold tabular-nums truncate text-amber-700">{formatCurrency(data?.stock?.valeur_achat)}</div>
              </div>
              <div className="pl-3 min-w-0">
                <div className="text-[11px] text-gray-500 mb-0.5">Vente</div>
                <div className="text-lg font-bold tabular-nums truncate text-blue-700">{formatCurrency(data?.stock?.valeur_vente)}</div>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-100 text-[11px] text-gray-500 text-center">
              {data?.stock?.qty_total || 0} article(s) · {data?.stock?.en_stock || 0} produit(s) en stock ·{' '}
              <Link to="/products" onClick={(e) => e.stopPropagation()} className="font-semibold text-red-600 hover:text-red-800">
                {data?.stock?.rupture || 0} rupture(s)
              </Link>
            </div>
          </div>

          {/* Stock faible — carte sombre bordée rouge (rouge = problème ; hors
              livre, même grammaire que les cartes KPI). */}
          <div className="mk-dark rounded-2xl p-4 bg-gray-800 border border-red-600 shadow-[0_0_12px_theme(colors.red.600/18%)]">
            <div className="flex items-center justify-between mb-3 gap-2">
              <h2 className="font-semibold text-green-50 flex items-center gap-2 min-w-0">
                <IcAlertTriangle className="text-red-400 shrink-0" /> <span className="truncate">Stock faible</span>
              </h2>
              <Link to="/products" className="text-sm font-semibold text-red-400 hover:text-red-300 shrink-0">Gérer →</Link>
            </div>
            <div className="space-y-2">
              {(!data?.low_stock_products || data.low_stock_products.length === 0) ? (
                <div className="text-center py-6"><IcCheckCircle className="text-4xl text-green-600 mx-auto mb-2" /><p className="text-gray-400 text-sm">Tous les stocks sont bons</p></div>
              ) : data.low_stock_products.slice(0, 8).map((p) => (
                <div key={p.id} className={`${DARK_ROW} p-3 flex justify-between items-center gap-2`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-200 truncate">{p.name}</p>
                    <p className="text-xs text-gray-400">Stock : <span className="font-bold text-red-400">{p.stock}</span> · Min/sem : {p.stock_min}{p.sold_qty != null && <span> · {p.sold_qty} vendus (30j)</span>}</p>
                  </div>
                  <IcAlertTriangle className="text-red-400 shrink-0" />
                </div>
              ))}
            </div>
          </div>

          {/* Sessions du jour (read-only overview) */}
          <Card title="Sessions du jour" icon={IcClock} iconClass="text-green-600"
            action={<Link to="/reservations" className="text-sm font-semibold text-green-600 hover:text-green-500 shrink-0">Voir tout →</Link>}>
            <div className="space-y-2">
              {(!data?.today_sessions || data.today_sessions.length === 0) ? (
                <div className="text-center py-6"><IcCalendarCheck className="text-4xl text-gray-600 mx-auto mb-2" /><p className="text-gray-400 text-sm">Aucune session aujourd'hui</p></div>
              ) : [...data.today_sessions].sort((a, b) => statusOrder(a.status) - statusOrder(b.status)).slice(0, 8).map((s) => (
                <div key={s.id} className={`${DARK_ROW} p-3`}>
                  <div className="flex justify-between items-start mb-1.5 gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-200 truncate">{s.customer_name}</p>
                      {s.customer_phone && <p className="text-xs text-gray-400 truncate"><PhoneLink phone={s.customer_phone} linkClassName="text-blue-400" /></p>}
                    </div>
                    <span className={`bdg shrink-0 ${getStatusColor(s.status)} px-2 py-1 rounded-full`}>{s.status}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap text-sm">
                    <span className="text-gray-200 font-medium truncate">{s.service_name}</span>
                    <span className="ml-auto text-xs text-gray-400 flex items-center gap-1"><IcClock size={10} /> {formatTime(s.scheduled_time)}</span>
                    <span className="font-semibold text-green-50 tabular-nums">{formatCurrency(s.service_price)}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Réservation de produits */}
          <Card title="Réservation de produits" icon={IcClipboardList} iconClass="text-blue-400"
            action={<Link to="/reservations" className="text-sm font-semibold text-green-600 hover:text-green-500 shrink-0">Voir tout →</Link>}>
            <div className="space-y-2">
              {(!data?.reserved_products || data.reserved_products.length === 0) ? (
                <div className="text-center py-6"><IcClipboardList className="text-4xl text-gray-600 mx-auto mb-2" /><p className="text-gray-400 text-sm">Aucune réservation produit</p></div>
              ) : data.reserved_products.slice(0, 8).map((r) => (
                <div key={r.id} className={`${DARK_ROW} p-3`}>
                  <div className="flex justify-between items-start mb-1 gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-200 truncate">{r.product_name} <span className="text-gray-500 font-normal">× {r.quantity}</span></p>
                      <p className="text-xs text-gray-400 truncate">{r.customer_name}{r.customer_phone ? <> · <PhoneLink phone={r.customer_phone} linkClassName="text-blue-400" /></> : null}</p>
                    </div>
                    {r.expected_arrival_date && <span className="text-xs text-gray-400 flex items-center gap-1 shrink-0"><IcClock size={10} /> {formatDate(r.expected_arrival_date)}</span>}
                  </div>
                  <div className="flex items-center justify-between">
                    {/* Étiquettes §6 : en attente (Or) / arrivée (Sapphire), texte seul. */}
                    <span className={`bdg ${r.status === 'arrived' ? 'bdg-arrive' : 'bdg-wait'} px-2 py-0.5 text-[11px] rounded-full`}>{r.status === 'arrived' ? 'Arrivée' : 'En attente'}</span>
                    {Number(r.deposit_amount) > 0 && <span className="text-xs text-gray-400">Avance <span className="font-semibold text-gray-200 tabular-nums">{formatCurrency(r.deposit_amount)}</span></span>}
                  </div>
                  <ReservationProductActions reservation={r} onChanged={refreshHome} dark className="mt-2 pt-2 border-t border-gray-700 justify-end" />
                </div>
              ))}
            </div>
          </Card>

          {/* Top 10 clients (loyalty points) — lien « Clients → » amber (règle
              Clients standard : amber-300 sur fond sombre). */}
          <Card title="Top 10 points clients" icon={IcTrophy} iconClass="text-amber-500"
            action={<Link to="/clients" className="text-sm font-semibold text-amber-300 hover:text-amber-200 shrink-0">Clients →</Link>}>
            <div className="space-y-2">
              {(!data?.top_clients || data.top_clients.length === 0) ? (
                <div className="text-center py-6"><IcUsers className="text-4xl text-gray-600 mx-auto mb-2" /><p className="text-gray-400 text-sm">Aucun client</p></div>
              ) : data.top_clients.slice(0, 10).map((c, i) => (
                <div key={c.id} className={`${DARK_ROW} flex items-center gap-3 p-2.5`}>
                  <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${rankColor(i)}`}>{i + 1}</span>
                  <p className="font-semibold text-gray-200 truncate flex-1 min-w-0">{c.name}</p>
                  <span className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-amber-300 tabular-nums"><IcCoins size={12} /> {Number(c.points || 0).toFixed(0)}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
